import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress } from "@mui/material";
import clientPreferenceImage from "../../assets/img/client-preference-img.svg";
import configurationImage from "../../assets/img/configuration-img.svg";
import testIntegrationImage from "../../assets/img/test-integration-img.svg";
import { getClientInfo } from "../../api";
import "./WelcomePage.css";

const WelcomePage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchClientType = async () => {
            try {
                // Get clientInfo from localStorage
                const storedClientInfo = localStorage.getItem("clientInfo");
                if (storedClientInfo) {
                    const clientInfo = JSON.parse(storedClientInfo);
                    if (clientInfo.clientType !== "CLIENT_PUSH")
                        navigate("/dashboard");
                    return;
                }
                // Get idTokenData from localStorage
                const storedIdTokenData = localStorage.getItem("idTokenData");
                if (!storedIdTokenData) {
                    throw new Error("No stored token data found");
                }

                const idTokenData = JSON.parse(storedIdTokenData);
                const clientCode = idTokenData?.clientCode;

                if (!clientCode) {
                    throw new Error("Client Code not found");
                }

                const response = await getClientInfo(clientCode);
                if (!response.data) return navigate("/dashboard");
                localStorage.setItem(
                    "clientInfo",
                    JSON.stringify(response.data),
                );
                const type = response.data.clientType;
                if (type !== "CLIENT_PUSH") navigate("/dashboard");
            } catch (err) {
                setError(err.message || "Failed to fetch client type");
                console.error("Error fetching client type:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchClientType();
    }, [navigate]);

    if (loading) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="100vh"
            >
                <CircularProgress />
                <Typography variant="h5" mt={2}>
                    Loading...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="100vh"
            >
                <Typography color="error" variant="h5">
                    {error}
                </Typography>
            </Box>
        );
    }

    const handleGetStarted = () => {
        navigate("/api-testing");
    };

    return (
        <div className="welcome-page">
            <div className="welcome-content">
                <h1 className="welcome-title">
                    Welcome to the Hotel Trader Self-serve Integration Platform
                </h1>
                <p className="welcome-description">
                    Your centralised workspace to configure and test your
                    integration with Hotel Trader.
                </p>
                <p className="welcome-subtitle">
                    Whether you're just getting started or enhancing your
                    existing connection, this platform enables you to manage
                    your integration setup and validate your API workflows with
                    ease.
                </p>

                <div className="features-container">
                    <div className="feature-card">
                        <div className="feature-icon configure-icon">
                            <img
                                src={configurationImage}
                                alt="config"
                                style={{ width: "36px" }}
                            />
                        </div>
                        <h3>Configure system details</h3>
                        <p>
                            Provide your API endpoints for Content and Audit
                            API, along with Basic Authentication credentials.
                        </p>
                    </div>

                    <div className="feature-card">
                        <div className="feature-icon preferences-icon">
                            <img
                                src={clientPreferenceImage}
                                alt="clientPref"
                                style={{ width: "36px" }}
                            />
                        </div>
                        <h3>Set your preferences</h3>
                        <p>
                            Define your support for key features, pricing models
                            and data types to ensure you only get hotel content
                            that aligns with your systems capabilities.
                        </p>
                    </div>

                    <div className="feature-card">
                        <div className="feature-icon test-icon">
                            <img
                                src={testIntegrationImage}
                                alt="testIntegrationImage"
                                style={{ width: "36px" }}
                            />
                        </div>
                        <h3>Test your integration</h3>
                        <p>
                            Trigger live requests to your endpoints and review
                            request/response payloads to validate integration
                            during the development phase.
                        </p>
                    </div>
                </div>

                <button
                    className="get-started-button"
                    onClick={handleGetStarted}
                >
                    Get started <span>{">"}</span>
                </button>
            </div>
        </div>
    );
};

export default WelcomePage;
